import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  faCircleUser,
} from '@fortawesome/free-regular-svg-icons';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import {
  IAnnualGoalsAddressed,
  ICptCodesAddressed,
  PresentDialogResult,
  PresentDialogStudentInfo,
  StudentPresentSessionDetails,
} from '../../interfaces/service-entry-details';
import { EcounterEntryService } from '../../services/ecounter-entry.service';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { ReferenceService } from 'src/app/services/reference.service';
import { LoadingService } from 'src/app/services/loading.service';
import { StudentDocument } from '../../interfaces/student-document';
import { DocumentService } from 'src/app/services/document.service';
import { PdfAndImageService } from 'src/app/services/pdf-image.service';

@Component({
  selector: 'app-encounter-attendance-present',
  templateUrl: './encounter-attendance-present.component.html',
  styleUrls: ['./encounter-attendance-present.component.scss'],
  standalone: false,
})
export class EncounterAttendancePresentComponent
  extends DialogContentBase
  implements OnInit {
  comparePreviousSessionNotes: boolean = false;
  attendanceForm!: FormGroup;
  previousSessionNotesForm!: FormGroup;
  @Input() studentPresentDetails: StudentPresentSessionDetails | undefined;
  @Input() studentInfo: PresentDialogStudentInfo | undefined;
  @Input() showComparePreviousSessionNotes: boolean = true;
  @Input() existingDocuments: StudentDocument[] = [];
  @Input() requireReasonForDelay: boolean = false;
  @Input() isCptApplicable: boolean = false;
  @Input() existingDialogResult: PresentDialogResult | null = null;
  @Input() meetingStartTime: Date | null = null;
  annualGoals: IAnnualGoalsAddressed[] = [];
  cptCodes: ICptCodesAddressed[] = [];
  progressIndicators: IReferenceData[] = [];
  previousSessionDetails: StudentPresentSessionDetails | null = null;
  providerSk: string = '';
  previousSessions: StudentPresentSessionDetails[] = [];
  selectedHistory: StudentPresentSessionDetails | null = null;
  firstSessionForm!: FormGroup;
  readonly sessionNotesMinValue: number = 20;
  readonly sessionNotesMaxValue: number = 8000;
  showAutoFilledCheckboxes: boolean = false;
  showCertifyingWarningMessage: boolean = false;
  showDelayInFirstAttend: boolean = false;
  showCptCodeError: boolean = false;
  reasonForDelayList: IReferenceData[] = [];
  readonly otherReasonForDelayId: number = 28;
  readonly reassignReason: number = 27;
  delayForm!: FormGroup;
  selectedAttachments: File[] = [];
  existingDocsToShow: string[] = [];
  @Input() serviceTypeDescription: string = '';
  @Input() serviceDurationMinutes: number = 0;
   faCircleUser = faCircleUser;
  private readonly ptOtServiceTypeIds: number[] = [
    6, // PT
    7, // OT
  ];

  constructor(
    dialog: DialogRef,
    public documentService: DocumentService,
    private pdfAndImageService: PdfAndImageService,
    private library: FaIconLibrary,
    private fb: FormBuilder,
    private encounterEntryService: EcounterEntryService,
    private referenceService: ReferenceService,
    private loadingService: LoadingService,
  ) {
    super(dialog);
    this.library.addIcons(faCircleUser, faTriangleExclamation);
  }

  ngOnInit(): void {
    this.loadingService.show();
    this.buildForm();
    this.cptCodesFA.valueChanges.subscribe(() => {
    });

    this.selectedAttachments = [];
    this.existingDocsToShow = (this.existingDocuments ?? [])
      .map((d: StudentDocument) => d.documentName)
      .filter((x): x is string => !!x && x.trim() !== '');

    this.attendanceForm.get('attachments')?.setValue([], { emitEvent: false });
    this.buildDelayForm();
    this.buildFirstSessionForm();
    this.buildPreviousSessionNotesForm();
    this.initializeFirstFlags();
    this.initializeCheckboxes();
    this.loadAnnualGoals();
    this.loadCptCodes();
    this.loadProgressIndicators();
    this.loadReasonForDelay();
    if (this.requireReasonForDelay) {
      this.attendanceForm
        .get('reasonForDelay')
        ?.setValidators([Validators.required]);
      this.attendanceForm
        .get('reasonForDelay')
        ?.updateValueAndValidity({ emitEvent: false });
    }

    this.delayForm
      .get('reasonForDelay')
      ?.valueChanges.subscribe(
        (val: IReferenceData | number | string | null) => {
          this.onReasonForDelayChange(val);
        },
      );
  }

  public getCptErrorMessage(): string | null {
    if (!this.isCptApplicable) return null;

    const selectedCptCount = this.cptCodesFA.controls
      .filter(c => c.value === true)
      .length;

    // If not PT/OT → no error message
    const serviceTypeId: number = this.studentInfo?.serviceTypeId ?? 0;
    if (!this.ptOtServiceTypeIds.includes(serviceTypeId)) {
      return null;
    }

    if (this.serviceDurationMinutes >= 1 && this.serviceDurationMinutes <= 22) {
      return selectedCptCount > 1
        ? 'Error: Only 1 CPT code can be selected if the service duration is 1-22 minutes.'
        : null;
    }

    if (this.serviceDurationMinutes >= 23 && this.serviceDurationMinutes <= 37) {
      return selectedCptCount > 2
        ? 'Error: Only 2 CPT code can be selected if the service duration is 23-37 minutes.'
        : null;
    }

    if (this.serviceDurationMinutes >= 38 && this.serviceDurationMinutes <= 52) {
      return selectedCptCount > 3
        ? 'Error: Only 3 CPT code can be selected if the service duration is 38-52 minutes.'
        : null;
    }

    if (this.serviceDurationMinutes >= 53) {
      return selectedCptCount > 4
        ? 'Error: Only 4 CPT code can be selected if the service duration is 53 minutes or more'
        : null;
    }

    return null;
  }

  buildDelayForm(): void {
    this.delayForm = this.fb.group({
      reasonForDelay: [
        null,
        this.requireReasonForDelay ? Validators.required : [],
      ],
      reasonForDelayOthers: [''],
    });
  }

  private initializeFirstFlags(): void {
    const isCreateMode = (this.studentInfo as any)?.isCreateMode === true;

    if (isCreateMode) {
      const autoFirstAttend = this.studentInfo?.isFirstAttend === true;
      const autoFirstEncounter = this.studentInfo?.isFirstEncounter === true;

      this.firstSessionForm.get('isFirstAttend')
        ?.setValue(autoFirstAttend, { emitEvent: false });
      this.firstSessionForm.get('isFirstEncounter')
        ?.setValue(autoFirstEncounter, { emitEvent: false });

      this.showAutoFilledCheckboxes = autoFirstAttend || autoFirstEncounter;
      return;
    }

    if (this.studentPresentDetails?.isFirstAttend !== null) {
      this.firstSessionForm.get('isFirstAttend')
        ?.setValue(this.studentPresentDetails?.isFirstAttend, { emitEvent: false });
    }

    if (this.studentPresentDetails?.isFirstEncounter !== null) {
      this.firstSessionForm.get('isFirstEncounter')
        ?.setValue(this.studentPresentDetails?.isFirstEncounter, { emitEvent: false });
    }

    this.showAutoFilledCheckboxes = false;
  }

  onReasonForDelayChange(val: IReferenceData | number | string | null): void {
    const ctl: AbstractControl | null = this.delayForm.get(
      'reasonForDelayOthers',
    );
    if (!ctl) {
      return;
    }

    const id: number = Number(
      val && typeof val === 'object' ? (val as IReferenceData).id : val,
    );

    const isOther: boolean = id === this.otherReasonForDelayId;
    if (isOther) {
      ctl.setValidators([Validators.required]);
    } else {
      ctl.clearValidators();
      ctl.setValue('', { emitEvent: false });
    }

    ctl.updateValueAndValidity({ emitEvent: false });
  }

  public get isOtherReasonSelected(): boolean {
    const val = this.delayForm?.get('reasonForDelay')?.value;
    const id: number = Number(
      val && typeof val === 'object' ? (val as IReferenceData).id : val,
    );
    return id === this.otherReasonForDelayId;
  }

  loadReasonForDelay(): void {
    this.referenceService
      .getDropDownValues('ReasonsForReject')
      .subscribe((res: IReferenceData[]) => {
        const all: IReferenceData[] = res || [];
        this.reasonForDelayList = all.filter(
          (item: IReferenceData) => item.id !== this.reassignReason,
        );
      });
  }

  loadProgressIndicators(): void {
    this.referenceService
      .getDropDownValues('EncounterProgressType')
      .subscribe((res: IReferenceData[]) => {
        this.progressIndicators = res || [];
      });
  }

  get hasExistingDocuments(): boolean {
    return (this.existingDocuments?.length ?? 0) > 0;
  }

  buildPreviousSessionNotesForm(): void {
    this.previousSessionNotesForm = this.fb.group({
      historySessionDate: [null],
      annualGoals: this.fb.array([]),
      cptCodes: this.fb.array([]),
      progressIndicator: [{ value: null }],
      sessionNotes: [{ value: ''}],
    });
  }

  buildFirstSessionForm(): void {
    this.firstSessionForm = this.fb.group({
      isFirstAttend: [this.studentInfo?.isFirstAttend ?? false],
      isFirstEncounter: [this.studentInfo?.isFirstEncounter ?? false],
    });
  }

  private applyExistingSelection(): void {
    if (!this.studentPresentDetails) {
      return;
    }

    // 1) Annual goals checkboxes
    const selectedGoals: IAnnualGoalsAddressed[] =
      this.studentPresentDetails.annualGoalsAddressed || [];
    this.annualGoals.forEach((goal: IAnnualGoalsAddressed, index: number) => {
      const selected: boolean = selectedGoals.some(
        (g: IAnnualGoalsAddressed) =>
          g.anualGoalId === goal.anualGoalId
        // || g.anualGoalMandateSk === goal.anualGoalMandateSk,
      );
      if (this.annualGoalsFA.at(index)) {
        this.annualGoalsFA.at(index).setValue(selected, { emitEvent: false });
      }
    });

    // CPT codes – only with cptCodesChecked === true
    const selectedCpts: ICptCodesAddressed[] = (
      this.studentPresentDetails.cptCodeAddressed || []
    ).filter((c: ICptCodesAddressed) => c.cptCodesChecked === true);

    this.cptCodes.forEach((cpt: ICptCodesAddressed, index: number) => {
      const selected: boolean = selectedCpts.some(
        (c: ICptCodesAddressed) =>
          c.cptCodesMappingId === cpt.cptCodesMappingId,
      );
      if (this.cptCodesFA.at(index)) {
        this.cptCodesFA.at(index).setValue(selected, { emitEvent: false });
      }
    });

    // 3) Progress indicator
    if (this.studentPresentDetails.progressIndicator) {
      this.attendanceForm
        .get('progressIndicator')
        ?.setValue(this.studentPresentDetails.progressIndicator.id, {
          emitEvent: false,
        });
    }

    // 4) Session notes
    if (this.studentPresentDetails.sessionNotes) {
      this.attendanceForm
        .get('sessionNotes')
        ?.setValue(this.studentPresentDetails.sessionNotes, {
          emitEvent: false,
        });
    }

    if (this.studentPresentDetails?.isFirstAttend !== null) {
      this.firstSessionForm
        .get('isFirstAttend')
        ?.setValue(this.studentPresentDetails.isFirstAttend, {
          emitEvent: false,
        });
    }

    if (this.studentPresentDetails?.isFirstEncounter !== null) {
      this.firstSessionForm
        .get('isFirstEncounter')
        ?.setValue(this.studentPresentDetails.isFirstEncounter, {
          emitEvent: false,
        });
    }
  }

  loadAnnualGoals(): void {
    const mandateSk: string = this.studentInfo?.mandateSk;
    if (!mandateSk) {
      return;
    }

    this.encounterEntryService
      .getAnnualGoalsLookup(mandateSk)
      .subscribe((goals: IAnnualGoalsAddressed[]) => {
        this.annualGoals = goals || [];
        this.initializeCheckboxes();
        this.applyExistingSelection();
        this.applyExistingDialogResult();
      });
  }

  initializeCheckboxes(): void {
    if (!this.annualGoalsFA || !this.cptCodesFA) return;

    this.annualGoalsFA.clear();
    this.cptCodesFA.clear();

    this.annualGoals.forEach(() => {
      this.annualGoalsFA.push(this.fb.control(false));
    });

    this.cptCodes.forEach(() => {
      this.cptCodesFA.push(this.fb.control(false));
    });
  }

  loadCptCodes(): void {
    if (!this.isCptApplicable) {
      this.cptCodes = [];
      this.resetCptCheckboxes();
      this.applyExistingDialogResult();
      this.loadingService.hide();
      return;
    }

    const serviceTypeId: number = this.studentInfo?.serviceTypeId;
    const studentSk: string = this.studentInfo?.studentSk;

    if (!serviceTypeId || !studentSk) {
      this.loadingService.hide();
      return;
    }

    const body: {
      encounterSk: string | null;
      studentSk: string[];
      serviceTypSk: number;
    } = {
      encounterSk: this.studentInfo.encounterSk ?? null,
      studentSk: [studentSk],
      serviceTypSk: serviceTypeId,
    };

    this.encounterEntryService.getCptCodesGroupList(body).subscribe({
      next: (codes: ICptCodesAddressed[]) => {
        this.cptCodes = (codes || []).filter(c => c.isIndividual === true);
        this.resetCptCheckboxes();
        this.applyExistingSelection();
        const cptArray = this.attendanceForm.get('cptCodes') as FormArray;

        if (this.isCptApplicable && this.cptCodes.length > 0) {
          cptArray.setValidators(this.atLeastOneChecked.bind(this));
        } else {
          cptArray.clearValidators();
        }
        cptArray.updateValueAndValidity({ emitEvent: false });
        this.applyExistingDialogResult();
        this.loadingService.hide();
      },
      error: () => {
        this.loadingService.hide();
      },
    });
  }

  resetCptCheckboxes(): void {
    this.cptCodesFA.clear();
    this.cptCodes.forEach(() => {
      this.cptCodesFA.push(this.fb.control(false));
    });
  }

  get annualGoalsFA(): FormArray {
    const control: FormArray = this.attendanceForm.get(
      'annualGoals',
    ) as FormArray;
    return control ? control : this.fb.array([]);
  }

  get cptCodesFA(): FormArray {
    const control: FormArray = this.attendanceForm.get('cptCodes') as FormArray;
    return control ? control : this.fb.array([]);
  }

  buildForm(): void {
    this.attendanceForm = this.fb.group({
      annualGoals: this.fb.array([], this.atLeastOneChecked.bind(this)),
      cptCodes: this.fb.array([]),
      progressIndicator: [null, Validators.required],
      sessionNotes: [
        '',
        [
          Validators.required,
          Validators.minLength(this.sessionNotesMinValue),
        ],
      ],
      attachments: [[] as File[]],
    });
  }

  onFilesChange(files: File[]): void {
    const incoming: File[] = Array.isArray(files) ? files : [];
    const merged: File[] = [...this.selectedAttachments, ...incoming];
    const unique: File[] = merged.filter(
      (file, index, arr) =>
        index === arr.findIndex(
          (x) =>
            x.name === file.name &&
            x.size === file.size &&
            x.lastModified === file.lastModified,
        ),
    );

    this.selectedAttachments = unique;
    this.attendanceForm.get('attachments')?.setValue(unique, { emitEvent: false });
  }

  getGoalControl(i: number): FormControl {
    return this.annualGoalsFA.at(i) as FormControl;
  }

  getCptControl(i: number): FormControl {
    return this.cptCodesFA.at(i) as FormControl;
  }

  getPreviousSessionGoalControl(i: number): FormControl {
    const array: FormArray = this.previousSessionNotesForm.get(
      'annualGoals',
    ) as FormArray;
    while (array.length <= i) {
      array.push(this.fb.control({ value: false, disabled: true }));
    }
    return array.at(i) as FormControl;
  }

  getPreviousSessionCptControl(i: number): FormControl {
    const array: FormArray = this.previousSessionNotesForm.get(
      'cptCodes',
    ) as FormArray;
    while (array.length <= i) {
      array.push(this.fb.control({ value: false, disabled: true }));
    }
    return array.at(i) as FormControl;
  }

  private atLeastOneChecked(control: AbstractControl): ValidationErrors | null {
    const arr: FormArray = control as FormArray;
    const hasOne: boolean = arr.controls.some((c) => !!c.value);
    return hasOne ? null : { atLeastOne: true };
  }

  onSave(): void {
    this.attendanceForm.markAllAsTouched();

    if (this.requireReasonForDelay) {
      this.delayForm.markAllAsTouched();
    }

    if (this.attendanceForm.invalid) {
      return;
    }

    if (this.requireReasonForDelay && this.delayForm.invalid) {
      return;
    }

    const studentSk: string = this.studentInfo?.studentSk;

    const selectedAnnualGoals: IAnnualGoalsAddressed[] = this.annualGoals
      .map((g: IAnnualGoalsAddressed, i: number) =>
        this.annualGoalsFA.at(i).value ? g : null,
      )
      .filter((x: IAnnualGoalsAddressed | null) => !!x);

    const selectedCptCodes: ICptCodesAddressed[] = this.cptCodes
      .map((c: ICptCodesAddressed, i: number) =>
        this.cptCodesFA.at(i).value ? c : null,
      )
      .filter((x: ICptCodesAddressed | null) => !!x);
    const progress = this.attendanceForm.get('progressIndicator')?.value;
    const sessionNotes: string = this.attendanceForm.get('sessionNotes')?.value;

    const progressTypeId: number =
      typeof progress === 'object' && progress !== null
        ? progress.id
        : progress;

    const attachments: File[] = this.selectedAttachments ?? [];

    const reasonVal = this.delayForm.get('reasonForDelay')?.value;
    const reasonForDelayId: number | null = (() => {
      const v: any = this.delayForm.get('reasonForDelay')?.value;
      if (!v) {
        return null;
      }
      if (typeof v === 'object') {
        return Number((v as IReferenceData).id);
      }
      return Number(v);
    })();

    const reasonForDelay: string =
      reasonVal && typeof reasonVal === 'object'
        ? (reasonVal as IReferenceData).description
        : reasonVal
          ? String(reasonVal)
          : '';

    const reasonForDelayOthers: string =
      this.delayForm.get('reasonForDelayOthers')?.value ?? '';

    const result: {
      studentSk: string;
      isFirstAttend: boolean;
      isFirstEncounter: boolean;
      annualGoals: IAnnualGoalsAddressed[];
      cptCodes: ICptCodesAddressed[];
      progressTypeId: number;
      sessionNotes: string;
      attachments: File[];
      reasonForDelayId: number | null;
      reasonForDelay: string;
      reasonForDelayOthers: string;
    } = {
      studentSk,
      isFirstAttend: this.firstSessionForm.get('isFirstAttend')?.value ?? false,
      isFirstEncounter:
        this.firstSessionForm.get('isFirstEncounter')?.value ?? false,
      annualGoals: selectedAnnualGoals,
      cptCodes: selectedCptCodes,
      progressTypeId: progressTypeId ?? null,
      sessionNotes: sessionNotes ?? '',
      attachments,
      reasonForDelayId,
      reasonForDelay,
      reasonForDelayOthers,
    };

    this.dialog.close(result);
  }

  public get canSave(): boolean {
    if (!this.attendanceForm) {
      return false;
    }

    if (this.attendanceForm.invalid) {
      return false;
    }

    if (this.getCptErrorMessage()) return false;

    if (!this.requireReasonForDelay) {
      return true;
    }

    if (!this.delayForm || this.delayForm.invalid) {
      return false;
    }

    return true;
  }

  onComparePreviousToggle(event: boolean): void {
    this.comparePreviousSessionNotes = event;
    if (event) {
      this.loadingService.show();
      this.loadPreviousSessionDetails();
    } else {
      this.clearPreviousSessionData();
    }
  }

  private loadPreviousSessionDetails(): void {
    this.encounterEntryService
      .getStudentPreviousSessionDetails(
        this.providerSk,
        this.studentInfo.studentSk,
        this.studentInfo.encounterSk,
      )
      .subscribe({
        next: (data: StudentPresentSessionDetails[]) => {
          this.previousSessions = data || [];

          if (this.previousSessions.length) {
            const first: StudentPresentSessionDetails =
              this.previousSessions[0];

            this.previousSessionNotesForm
              .get('historySessionDate')
              ?.setValue(first, { emitEvent: false });

            this.populatePreviousSessionForm(first);
          }

          this.loadingService.hide();
        },
        error: () => {
          this.comparePreviousSessionNotes = false;
          this.clearPreviousSessionData();
          this.loadingService.hide();
        },
      });
  }

  onHistoryChange(session: StudentPresentSessionDetails | null): void {
    if (session) {
      this.populatePreviousSessionForm(session);
    } else {
      this.clearPreviousSessionData();
    }
  }

  private populatePreviousSessionForm(
    session: StudentPresentSessionDetails,
  ): void {
    const prevAnnualGoals: FormArray = this.previousSessionNotesForm.get(
      'annualGoals',
    ) as FormArray;
    const prevCptCodes: FormArray = this.previousSessionNotesForm.get(
      'cptCodes',
    ) as FormArray;

    prevAnnualGoals.clear();
    prevCptCodes.clear();

    this.annualGoals.forEach((goal: IAnnualGoalsAddressed) => {
      const wasSelected: boolean =
        session?.annualGoalsAddressed?.some(
          (g: IAnnualGoalsAddressed) =>
            g.anualGoalId === goal.anualGoalId
          // || g.anualGoalMandateSk === goal.anualGoalMandateSk,
        ) ?? false;

      prevAnnualGoals.push(
        this.fb.control({ value: wasSelected, disabled: true }),
      );
    });

    this.cptCodes.forEach((cpt: ICptCodesAddressed) => {
      const wasSelected: boolean =
        session?.cptCodeAddressed
          ?.filter((c: ICptCodesAddressed) => c.cptCodesChecked === true)
          ?.some((c: ICptCodesAddressed) => c.cptCode === cpt.cptCode) ?? false;

      prevCptCodes.push(
        this.fb.control({ value: wasSelected, disabled: true }),
      );
    });

    this.previousSessionNotesForm.patchValue({
      progressIndicator: session?.progressIndicator?.id ?? null,
      sessionNotes: session?.sessionNotes || '',
    });
  }

  private clearPreviousSessionData(): void {
    this.previousSessionNotesForm.reset();
    this.previousSessionDetails = null;
  }

  private applyExistingDialogResult(): void {
    if (!this.existingDialogResult) {
      return;
    }

    const r = this.existingDialogResult;

    // Annual goals
    if (this.annualGoalsFA && r.annualGoals?.length) {
      this.annualGoals.forEach((g, i) => {
        const selected = r.annualGoals!.some(
          x =>
            // x.anualGoalMandateSk === g.anualGoalMandateSk ||
            x.anualGoalId === g.anualGoalId
        );
        this.annualGoalsFA.at(i)?.setValue(selected, { emitEvent: false });
      });
    }

    // CPT codes
    if (this.cptCodesFA && r.cptCodes?.length) {
      this.cptCodes.forEach((c, i) => {
        const selected = r.cptCodes!.some(
          x =>
            x.cptCodesMappingId === c.cptCodesMappingId ||
            x.cptCodeMappingId === c.cptCodesMappingId
        );
        this.cptCodesFA.at(i)?.setValue(selected, { emitEvent: false });
      });
    }

    // Progress + notes
    if (r.progressTypeId) {
      this.attendanceForm.get('progressIndicator')?.setValue(r.progressTypeId, {
        emitEvent: false,
      });
    }
    if (r.sessionNotes !== null) {
      this.attendanceForm.get('sessionNotes')?.setValue(r.sessionNotes, {
        emitEvent: false,
      });
    }

    // First flags
    this.firstSessionForm
      .get('isFirstAttend')
      ?.setValue(r.isFirstAttend ?? false, { emitEvent: false });
    this.firstSessionForm
      .get('isFirstEncounter')
      ?.setValue(r.isFirstEncounter ?? false, { emitEvent: false });

    // Reason for delay
    this.delayForm
      .get('reasonForDelay')
      ?.setValue(r.reasonForDelayId ?? null, { emitEvent: false });
    this.delayForm
      .get('reasonForDelayOthers')
      ?.setValue(r.reasonForDelayOthers ?? '', { emitEvent: false });
  }
  downloadDocument(documentRow: StudentDocument): void {
    this.loadingService.show();
    this.documentService

      .getDocument(documentRow.documentRef)

      .subscribe((response: Uint8Array<ArrayBufferLike>) => {

        this.pdfAndImageService.download(

          response,

          documentRow.documentFormat,

          documentRow.documentName

        );
        this.loadingService.hide();
      });

  }
}
